import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  paymentAccountResponseSchema,
  paymentAccountSetSchema,
  type PaymentAccountSet,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';

/**
 * Data access for the Payment Account — the existing endpoints only:
 *   GET /api/payment-account   the one organisation-level record, or null (payment_account.view)
 *   PUT /api/payment-account   set or replace all three fields (payment_account.manage; audited)
 * Values travel only in request and response bodies — never in URLs, storage or logs.
 */
export const paymentAccountKeys = { all: ['payment-account'] as const };

export function usePaymentAccount(enabled = true) {
  return useQuery({
    enabled,
    queryKey: paymentAccountKeys.all,
    queryFn: async () =>
      (await apiRequest('/payment-account', { schema: paymentAccountResponseSchema }))
        .paymentAccount,
  });
}

export function useSetPaymentAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PaymentAccountSet) =>
      apiRequest('/payment-account', {
        method: 'PUT',
        body: paymentAccountSetSchema.parse(body),
        schema: paymentAccountResponseSchema,
      }),
    // Re-read the record from the API rather than trusting local form state.
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: paymentAccountKeys.all }),
  });
}
